import Vue from 'vue'
import JsonApiObject from '@/_models/JsonApi'

import PostProjectUser from '@/views/Authorization/dialogs/postProjectUser/postProjectUser.vue'
import RemoveUserRole from '@/views/Authorization/dialogs/removeUserRole/removeUserRole.vue'
import RemoveAccessUserRole from '@/views/Authorization/dialogs/removeUserAccessRole/removeUserAccessRole.vue'
import UserInfo from '@/views/Authorization/dialogs/userInfo/userInfo.vue'
import UserEdit from '@/views/Authorization/dialogs/userEdit/userEdit.vue'
import UserLog from '@/views/Authorization/dialogs/userLog/userLog.vue'

import Log from '@/views/Log/models/log'

import store from '@/store'
import router from '@/_router'

export default class ProjectUserModel extends JsonApiObject {
    static async search(query) {
        let res = await Vue.prototype.$vbit.get('authorization', 'projectUsers', query)

        let users = res.data.map((user) => new ProjectUserModel(user, res.included))

        return users
    }

    public constructor(data: any, included?) {
        super(data, included)
    }

    email
    jobTitle
    jobDescription
    invitationMessage
    createdAt
    updatedAt
    mobile

    get _project() {
        return this._relationships['project'] ? this._relationships['project'] : null
    }

    get project() {
        return this._project ? this._project.name : null
    }

    get isProjectUser() {
        return true
    }

    get _user() {
        return this._relationships['user'] ? this._relationships['user'] : null
    }

    get hasUser() {
        return this._user ? true : false
    }

    get userId() {
        return this.hasUser ? this._user._id : null
    }

    get isCurrentUser() {
        return this.hasUser ? this._user._id == store.getters.user.id : false
    }

    get primaryEmail() {
        return this.hasUser ? this._user.primaryEmail : this['email']
    }

    get userName() {
        return this.hasUser ? this._user.name : null
    }

    get avatar() {
        return this.hasUser ? this._user.avatar : null
    }

    get initials() {
        return this.hasUser ? this._user.initials : null
    }

    get mobileNumber() {
        return this.hasUser && this._user['mobileNumber'] ? this._user['mobileNumber'] : null
    }

    get phoneNumber() {
        return this.hasUser && this._user['phoneNumber'] ? this._user['phoneNumber'] : null
    }

    get displayContractor() {
        return this._relationships['displayContractor']
            ? this._relationships['displayContractor']
            : null
    }

    get contractorAssignments() {
        return this._relationships['contractorAssignments']
            ? this._relationships['contractorAssignments']
            : null
    }

    get projectContractor() {
        let name = this.displayContractor?.company?.data?.attributes?.name
        if (!name && Array.isArray(this.displayContractor?.namePath)) {
            name = this.displayContractor.namePath[this.displayContractor.namePath.length - 1]
        }
        return name
    }

    get _roles() {
        return this._relationships['roles'] ? this._relationships['roles'] : null
    }

    get hasRoles() {
        return this._roles ? (this._roles.length > 0 ? true : false) : false
    }

    get rolesArr() {
        return this._roles.map((role) => role._id)
    }

    hasRole(roleId: string) {
        return this.rolesArr.includes(roleId)
    }

    get accessRolesArr() {
        return this.rolesArr.filter((role) => store.getters.role(role).hasAuthorizationManageAccess)
    }

    get permissions() {
        let permissions: Array<string> = []
        this._roles.map((role) => {
            permissions = [
                ...permissions,
                ...(store.getters.role(role._id)
                    ? store.getters.role(role._id).permissions
                    : role.permissions),
            ]
        })
        return [...new Set(permissions)]
    }

    get permissionGroups() {
        return [...new Set(this.permissions.map((item) => item.split('.')[0]))].sort()
    }

    get grouppedPermissions() {
        return this.permissionGroups.map((group) =>
            this.permissions.filter((item) => item.includes(`${group}.`))
        )
    }

    async toggleRole(roleId: string) {
        let confirmed = true
        let isAccessRole = false
        // Dialog if remove current user role
        if (this.isCurrentUser && this.hasRole(roleId)) {
            if (this.accessRolesArr.includes(roleId) && this.accessRolesArr.length == 1) {
                confirmed = await JsonApiObject.openDialog(RemoveAccessUserRole, {
                    user: this,
                    role: roleId,
                })
                isAccessRole = true
            } else {
                confirmed = await JsonApiObject.openDialog(RemoveUserRole, {
                    user: this,
                    role: roleId,
                })
            }
        }

        if (!confirmed) {
            return
        }

        let userRoles = new Set(this._roles.map((role) => role._id))

        if (this.hasRole(roleId)) {
            userRoles.delete(roleId)
        } else {
            userRoles.add(roleId)
        }

        let data = {
            relationships: {
                roles: [...userRoles].map((role) => {
                    return { _type: 'role', _id: role }
                }),
            },
        }

        try {
            await this.patchProjectUser(data)
        } catch (error) {
            //
        }

        if (isAccessRole) {
            router.push({ name: 'user' })
        }
    }

    async editProjectUser(user, contractor?: { id: string; name: string }) {
        let confirm = await JsonApiObject.openDialog(UserEdit, { user: user, contractor })
        return confirm
    }

    async offboard() {
        let data = {
            relationships: {
                roles: [],
            },
        }

        return await this.patchProjectUser(data)
    }

    async patchProjectUser(data) {
        let body = this.formateBody(data.attributes, data.relationships)

        if (body) {
            let query = {
                include: [
                    'displayContractor',
                    'displayContractor.company',
                    'user',
                    'roles',
                    'contractorAssignments',
                    'contractorAssignments.contractor',
                    'contractorAssignments.roleAssignments',
                    'contractorAssignments.roleAssignments.role',
                ],
                updatedAt: this['updatedAt'],
                body: {
                    data: {
                        id: this._id,
                        type: this._type,
                        ...body,
                    },
                },
            }

            let res = await Vue.prototype.$vbit.patch(
                'authorization',
                `projectUsers/${this._id}`,
                query
            )

            this.initalize(res.data, res.included)

            return res
        }
    }

    async showUserInfo(user, project) {
        await JsonApiObject.openDialog(UserInfo, { user: user, project: project })
    }

    async showUserLog(user, project) {
        await JsonApiObject.openDialog(UserLog, { user: user, project: project })
    }

    async getProjectUserLog(projectId: string, timeStamp: any) {
        let data = {
            'filter[activityLogEntityId]': projectId,
            'filter[activityLogNumber]': ['12'],
            'filter[entityId]': this._id,
            'filter[time][lt]': timeStamp,
        }

        let res = await Log.getLog(data)

        return res
    }

    static async getProjectUserLog(projectUserId: string, projectId: string, timeStamp: any) {
        let data = {
            'filter[activityLogEntityId]': projectId,
            'filter[activityLogNumber]': ['12'],
            'filter[entityId]': projectUserId,
            'filter[time][lt]': timeStamp,
        }

        let res = await Log.getLog(data)

        return res
    }

    static async searchProjectUser(query) {
        let res = await Vue.prototype.$vbit.get('authorization', 'projectUsers', query)

        return res ? res.data.map((user) => new ProjectUserModel(user, res.included)) : []
    }

    static async getProjectUser(project: string, user: string, toStore = false) {
        let query = {
            include: [
                'user',
                'roles',
                'displayContractor',
                'displayContractor.company',
                'contractorAssignments',
                'contractorAssignments.contractor',
                'contractorAssignments.roleAssignments',
                'contractorAssignments.roleAssignments.role',
            ],
            filters: {
                'filter[userKindIfConfirmed]': 'normal',
                'filter[project]': project,
                'filter[user]': user,
            },
        }
        let res = await Vue.prototype.$vbit.get('authorization', `projectUsers`, query)

        let projectUser = res.data.map((user) => new ProjectUserModel(user, res.included))[0]
        if (toStore) {
            store.commit('projectUser', projectUser ? projectUser : null)
        }

        return projectUser
    }

    static async initiatePostProjectUser(
        user,
        role,
        project,
        roles?,
        contractor?: { id: string; name: string }
    ) {
        return await JsonApiObject.openDialog(PostProjectUser, {
            user: user,
            role: role,
            project: project,
            roles: roles,
            contractor,
        })
    }

    static async postProjectUser(data, contractor?: { id: string; name: string }, dryRun = false) {
        if (data) {
            let query = {
                include: [
                    'displayContractor',
                    'displayContractor.company',
                    'user',
                    'roles',
                    'contractorAssignments',
                    'contractorAssignments.contractor',
                    'contractorAssignments.roleAssignments',
                    'contractorAssignments.roleAssignments.role',
                ],
                updatedAt: this['updatedAt'],
                body: { data: data },
                ...(contractor?.id && { filters: { initialContractorId: contractor.id } }),
                ...(dryRun && { dryRun: true }),
            }

            let res = await Vue.prototype.$vbit.post('authorization', 'projectUsers', query)

            if (dryRun) {
                return
            }

            let projectUser = new ProjectUserModel(res.data, res.included)
            store.commit('projectUser', projectUser)
            return projectUser
        } else {
            return true
        }
    }
}
